import React, { useContext, useEffect, useState } from "react";
import {
	Box,
	FormControlLabel,
	Switch,
	Typography,
	Divider,
	Tooltip,
} from "@mui/material";
import TPLSlidePanel from "./TPLSlidePanel";
import Slider from "@mui/material/Slider";
import { MapViewContext } from "../../contexts/MapViewContext";
import FeatureLayer from "@arcgis/core/layers/FeatureLayer";
import {
	customPrioritizationLayerColors,
	statsServiceURL,
	dataFields
} from "../../constants";
import { prioritizeToolTips } from "../tooltipText";
import ClassBreaksRenderer from "@arcgis/core/renderers/ClassBreaksRenderer.js";
import CityContext from "../../contexts/CityContext";
import { PrioritizeSlidersContext } from "../../contexts/PrioritizeSlidersContext";
import { AlignmentContext } from "../../contexts/AlignmentContext";
import { trackEvent } from "../../hooks/useAnalytics";

export default function NewPrioritize() {
	const {
		prioritizeSlidersValue,
		setPrioritizeSlidersValue,
		checked,
		setChecked,
		hideOtherLayers,
		setHideOtherLayers,
	} = useContext(PrioritizeSlidersContext);
	const { alignment } = useContext(AlignmentContext);
	const { city, urban } = useContext(CityContext);
	const [totalCount, setTotalCount] = useState<number[]>([]);
	const [breaks, setBreaks] = useState<number[]>([0, 0]);
	const mV = useContext(MapViewContext);

	// The prioritizeFL object represents the new feature layer used to display the results of the user-set prioritization sliders
	let prioritizeFL = new FeatureLayer({
		url: statsServiceURL,
		title: "Custom Prioritization",
		id: "prioritize",
		visible: false,
		opacity: 0.62,
	});

	// The new layer is rendered using the same class breaks as the default prioritize layer
	let newRenderer: ClassBreaksRenderer;

	function getColors() {
		prioritizeFL.queryFeatureCount().then(function (results) {
			let step = Math.round(results / 3);
		});

		prioritizeFL.queryFeatures().then(function (resp) {
			let testEx: any[] = [];
			resp.features.forEach((feature: any) => {
				let totalFeature: any;

				// The custom prioritize value is calculated by multiplying the value of each slider with each feature's corresponding attribute and summing the results
				totalFeature =
					feature.attributes[dataFields["PPARank"]] * prioritizeSlidersValue["PPARank"] +
					feature.attributes[dataFields["LowIncPercRank"]] * prioritizeSlidersValue["LowIncPercRank"] +
					feature.attributes[dataFields["POCPercRank"]] * prioritizeSlidersValue["POCPercRank"] +
					feature.attributes[dataFields["HeatRank"]] * prioritizeSlidersValue["HeatRank"] +
					feature.attributes[dataFields["RESPRank"]] * prioritizeSlidersValue["RespRank"] +
					feature.attributes[dataFields["MentalRank"]] * prioritizeSlidersValue["MentalRank"] +
					feature.attributes[dataFields["LPARank"]] * prioritizeSlidersValue["LPARank"];

				testEx.push(totalFeature);

				if (testEx.length == resp.features.length) {
					setRenderer(testEx);
					setTotalCount(testEx);
				}
			});

			prioritizeFL.visible = true;
		});
	}

	function defQueryAndSort(view: any) {
		let defQueryOutside = checked ? "" : ` AND ${dataFields["Outside10MWOnly"]} = 1`;
		prioritizeFL = view.map.findLayerById("prioritize");

		if (
			alignment == "city" ||
			alignment == "park" ||
			(alignment == "evaluate" && city)
		) {
			getColors();
			prioritizeFL.definitionExpression = `${dataFields["PlaceID"]} = '${city?.attributes[dataFields["ID"]]}' ${defQueryOutside}`;
		} else if (
			alignment == "urban" ||
			alignment == "urban" ||
			(alignment == "evaluate" && urban)
		) {
			getColors();
			prioritizeFL.definitionExpression = `${dataFields["UA_ID"]} = '${urban?.attributes[dataFields["UA_ID"]]}' ${defQueryOutside}`;
		} else {
			prioritizeFL.definitionExpression = `${dataFields["Outside10MWOnly"]} = 1`;
		}
	}

	useEffect(() => {
		mV?.when((view: __esri.MapView) => {
			if (view.map.findLayerById("prioritize")) {
				defQueryAndSort(view);
			} else {
				view.map.add(prioritizeFL, 0);
				defQueryAndSort(view);
			}
		});
	}, [checked, alignment]);

	useEffect(() => {
		mV?.when((view: __esri.MapView) => {
			defQueryAndSort(view);
		});
	}, [prioritizeSlidersValue]);

	function setRenderer(testEx: any[]) {
		const tot = testEx.sort(function (a: any, b: any) {
			return a - b;
		});
		const step1 = Math.round(testEx.length / 3);
		const step2 = step1 + step1;
		const val = tot[step1];
		const val2 = tot[step2];
		setBreaks([val, val2]);

		// @ts-ignore
		const moderate = {
			type: "simple-fill", // autocasts as new SimpleFillSymbol()
			color: [196, 130, 250, 0.8],
			style: "solid",
			outline: {
				width: 0.1,
				color: [196, 130, 250, 0.8],
			},
		};

		// @ts-ignore
		const high = {
			type: "simple-fill", // autocasts as new SimpleFillSymbol()
			color: [163, 57, 250, 0.9],
			style: "solid",
			opacity: 0.1,
			outline: {
				width: 0.1,
				color: [163, 57, 250, 0.9],
			},
		};

		// @ts-ignore
		const veryHigh = {
			type: "simple-fill", // autocasts as new SimpleFillSymbol()
			color: [140, 5, 250, 0.95],
			style: "solid",
			opacity: 0.05,
			outline: {
				width: 2,
				color: "#6003ab",
			},
		};

		newRenderer = {
			type: "class-breaks",
			// @ts-ignore
			defaultSymbol: {
				// @ts-ignore
				type: "simple-fill", // autocasts as new SimpleFillSymbol()
				//@ts-ignore
				color: "black",
				style: "backward-diagonal",
				outline: {
					width: 0.5,
					color: [50, 50, 50, 0.6],
				},
			},
			label: "U.S. County",
			valueExpression: `
        var rank = (
		  ($feature.${dataFields["PPARank"]} * ${prioritizeSlidersValue["PPARank"]}) +
		  ($feature.${dataFields["LowIncPercRank"]} * ${prioritizeSlidersValue["LowIncPercRank"]}) +
		  ($feature.${dataFields["POCPercRank"]}  * ${prioritizeSlidersValue["POCPercRank"]}) +
		  ($feature.${dataFields["HeatRank"]} * ${prioritizeSlidersValue["HeatRank"]}) +
		  ($feature.${dataFields["RESPRank"]} * ${prioritizeSlidersValue["RespRank"]}) +
		  ($feature.${dataFields["MentalRank"]} * ${prioritizeSlidersValue["MentalRank"]}) +
		  ($feature.${dataFields["LPARank"]} * ${prioritizeSlidersValue["LPARank"]})
		);
        
          return rank;`,

			classBreakInfos: [
				{
					minValue: 0,
					maxValue: val,
					label: "Moderate priority",
					// @ts-ignore
					symbol: moderate,
				},
				{
					minValue: val + 1,
					maxValue: val2,
					label: "High priority",
					// @ts-ignore
					symbol: high,
				},
				{
					minValue: val2 + 1,
					maxValue: 12000,
					label: "Very high priority",
					// @ts-ignore
					symbol: veryHigh,
				},
			],
		};

		prioritizeFL.renderer = newRenderer;
	}

	const handleToggleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		setChecked(event.target.checked);
		if (event.target.checked) {
			trackEvent("prioritize_toggles", "show_park_priority_areas");
		}
	};

	// The onChange function updates the variable for each slider attribute based on the new slider value
	const onChange = ({ newValue, name }: { newValue: any; name: any }) => {
		//console.log(newValue, name)
		//trackPrioritizeSlider(newValue, name);
		trackEvent("prioritize_slider", newValue, name);
		setPrioritizeSlidersValue({
			...prioritizeSlidersValue,
			[name]: newValue,
		});

		let sum = 0;
		for (let x in prioritizeSlidersValue) {
			sum = sum + prioritizeSlidersValue[x];
		}
	};

	const handleSubmit = (event: any) => {
		event.preventDefault();
	};

	const sliderTitleStyle = {
		marginTop: "2px",
		fontSize: 12,
		cursor: "pointer",
	};

	// This function handles updates to the toggle that hides/shows other layers on the map.
	const onToggleHideOtherLayers = (e: any) => {
		const checked = e.target.checked;
		const showLayerTitles = [
			"World Hillshade",
			"World Topographic Map",
			"Custom Prioritization",
			"Park and Trail Sketch",
			"walk layer",
		];

		switch (alignment) {
			case "city":
				showLayerTitles.push("ParkServe Place");
				break;
			case "urban":
				showLayerTitles.push("ParkServe Urban Area");
				break;
		}

		if (!checked) {
			showLayerTitles.push("Park with public access");
			showLayerTitles.push("10-minute walk service area");
			switch (alignment) {
				case "city":
					showLayerTitles.push("Priority areas for new parks (place)");
					break;
				case "urban":
					showLayerTitles.push("Priority areas for new parks (Urban Area)");
					break;
			}
		}
		setHideOtherLayers(checked);
		const layers = mV?.map?.allLayers;
		layers?.forEach((l) => {
			if (showLayerTitles.includes(l.title)) {
				l.visible = true;
			} else {
				l.visible = false;
			}
		});
		if (checked) {
			trackEvent("prioritize_toggles", "hide_other_layers");
		}
	};

	const PrioritizeSlider = ({
		onChange,
		name,
		text,
	}: //max,
	{
		onChange: any;
		name: string;
		text: string;
	}) => {
		const { prioritizeSlidersValue, setPrioritizeSlidersValue } = useContext(
			PrioritizeSlidersContext
		);
		const handleSliderChange = (event: any, newValue: any) => {
			onChange({ name, newValue });
		};

		const handleCommit = (event: any, newValue: any) => {
			onChange({ name, newValue });
		};

		return (
			<div>
				<Box sx={sliderTitleStyle}>
					<Tooltip
						title={prioritizeToolTips[name]}
						placement="right"
					>
						<Typography variant="body1">{text}</Typography>
					</Tooltip>
				</Box>

				<Box width="100%">
					<Slider
						min={0}
						max={100}
						step={10}
						value={prioritizeSlidersValue[name]}
						aria-label="Default"
						name={name}
						valueLabelDisplay="auto"
						getAriaValueText={(e) => `${e}%`}
						valueLabelFormat={(e) => `${e}%`}
						onChangeCommitted={handleCommit}
						sx={{
							"& .MuiSlider-track": {
								opacity: 0,
							},
							"& .MuiSlider-rail": {
								backgroundImage: "linear-gradient(.25turn, #c482fa, #8c05fa)",
								opacity: 1,
							},
							"& .MuiSlider-thumb": {
								height: 20,
								width: 20,
								backgroundColor: "#8c05fa",
							},
						}}
					/>
				</Box>
			</div>
		);
	};

	return (
		<TPLSlidePanel>
			<form onSubmit={handleSubmit}>
				<Box
					px={0.5}
					py={0.5}
					bgcolor="white"
					boxSizing="border-box"
				>
					<Box
						display="flex"
						flexDirection="column"
						alignContent="space-between"
						justifyContent="space-between"
					>
						<Box>
							<Typography
								variant="h2"
								color="grass.main"
								sx={{ marginBottom: "10px" }}
							>
								Prioritize Parks
							</Typography>
							<Typography
								variant="body1"
								fontSize="14px"
							>
								Customize the park priority areas by adjusting the weighting on
								the variables below.
							</Typography>
						</Box>
						<FormControlLabel
							control={
								<Switch
									checked={hideOtherLayers}
									onChange={(e) => onToggleHideOtherLayers(e)}
								/>
							}
							label="Hide other layers"
						/>

						<Box
							paddingLeft="2px"
							sx={{ marginTop: "10px" }}
						>
							<Box
								display="flex"
								justifyContent="space-around"
								flexDirection="row"
								sx={{ gap: "10px" }}
								px={2}
								py={1}
							>
								<Box
									display="flex"
									flexDirection="row"
									justifyContent="start"
									alignItems="center"
									fontSize="12px"
								>
									<Box
										sx={{
											height: "15px",
											width: "15px",
											backgroundColor: customPrioritizationLayerColors.moderate,
										}}
									/>{" "}
									<Box sx={{ marginLeft: "5px" }}>
										<Typography variant="body1">Moderate</Typography>
									</Box>
								</Box>

								<Box
									display="flex"
									flexDirection="row"
									justifyContent="start"
									alignItems="center"
									fontSize="12px"
								>
									<Box
										sx={{
											height: "15px",
											width: "15px",
											backgroundColor: customPrioritizationLayerColors.high,
										}}
									/>{" "}
									<Box sx={{ marginLeft: "5px" }}>
										<Typography variant="body1">High</Typography>
									</Box>
								</Box>
								<Box
									display="flex"
									flexDirection="row"
									justifyContent="start"
									alignItems="center"
									fontSize="12px"
								>
									<Box
										sx={{
											height: "15px",
											width: "15px",
											backgroundColor: customPrioritizationLayerColors.veryHigh,
										}}
									/>{" "}
									<Box sx={{ marginLeft: "5px" }}>
										<Typography variant="body1">Very High</Typography>
									</Box>
								</Box>
							</Box>
						</Box>
						<Divider sx={{ marginTop: "5px", marginBottom: "15px" }} />

						<Box marginLeft={"10px"}>
							<PrioritizeSlider
								onChange={onChange}
								name="LowIncPercRank"
								text="Low-income households (%)"
							/>
							<PrioritizeSlider
								onChange={onChange}
								name="POCPercRank"
								text="People of color (%)"
							/>
							<PrioritizeSlider
								onChange={onChange}
								name="HeatRank"
								text="Heat islands"
							/>
							<PrioritizeSlider
								onChange={onChange}
								name="RespRank"
								text="Air pollution"
							/>
							<PrioritizeSlider
								onChange={onChange}
								name="PPARank"
								text="Population density"
							/>
							<PrioritizeSlider
								onChange={onChange}
								name="MentalRank"
								text="Poor mental health"
							/>
							<PrioritizeSlider
								onChange={onChange}
								name="LPARank"
								text="Lack of physical activity"
							/>
						</Box>
					</Box>
					<Box
						display="flex"
						flexDirection="row"
						alignItems="center"
						sx={{
							backgroundColor: "sun.main",
							padding: "10px",
							marginTop: "20px",
						}}
					>
						<Typography variant="body1">
							Show park priority areas for the whole city, not just areas
							outside a 10-minute walk to a park
						</Typography>
						<Switch
							checked={checked}
							onChange={handleToggleChange}
						/>
					</Box>
				</Box>
			</form>
		</TPLSlidePanel>
	);
}
